import { useState, useEffect, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatFileSize } from "@/lib/clients";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Paperclip, Loader2, Send, X, Plus } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1";

export function EmailComposeDialog({ open, onOpenChange, client, replyData, onSent }) {
  const { user } = useAuth();
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [threadId, setThreadId] = useState("");
  const [docs, setDocs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open || !client) return;
    if (replyData) {
      const rs = replyData.subject || "";
      setTo((replyData.to || []).join(", "));
      setSubject(/^re:/i.test(rs) ? rs : `Re: ${rs}`);
      setBody(`\n\n----- Original message -----\n${replyData.body || ""}`);
      setThreadId(replyData.thread_id || "");
      setShowCc(Boolean((replyData.cc || []).length));
      setCc((replyData.cc || []).join(", "));
    } else {
      setTo(client.email || ""); setSubject(""); setBody(""); setThreadId(""); setShowCc(false); setCc("");
    }
    setBcc(""); setSelectedDocs([]); setNewFiles([]);
    api.get(`/clients/${client.id}/files`).then((r) => setDocs(r.data)).catch(() => setDocs([]));
    api.get("/email-templates").then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
  }, [open, client, replyData]);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setSubject(t.subject || "");
    setBody(t.body || "");
  };

  const toggleDoc = (id) =>
    setSelectedDocs((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const addNewFiles = (e) => {
    setNewFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
    if (fileRef.current) fileRef.current.value = "";
  };
  const removeNewFile = (idx) => setNewFiles((prev) => prev.filter((_, i) => i !== idx));

  // Upload one attachment in small chunks (bypasses the gateway request-size limit),
  // returning the staged attachment id assembled server-side.
  const CHUNK_SIZE = 1024 * 1024; // 1 MB
  const uploadOne = async (file, onProgress) => {
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    let stagedId = null;
    for (let i = 0; i < total; i++) {
      const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("chunk_index", i);
      fd.append("total_chunks", total);
      fd.append("filename", file.name);
      fd.append("content_type", file.type || "application/octet-stream");
      fd.append("chunk", blob, file.name);
      const { data } = await api.post(`/clients/${client.id}/email/attachments/chunk`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onProgress?.(Math.round(((i + 1) / total) * 100));
      if (data.done) stagedId = data.staged_id;
    }
    return stagedId;
  };

  const send = async () => {
    if (!to.trim()) { toast.error("Add at least one recipient"); return; }
    if (!subject.trim()) { toast.error("Add a subject"); return; }
    setSending(true);
    try {
      const stagedIds = [];
      for (let i = 0; i < newFiles.length; i++) {
        const f = newFiles[i];
        setUploadStatus(`Uploading ${f.name} (${i + 1}/${newFiles.length})…`);
        const sid = await uploadOne(f, (p) => setUploadStatus(`Uploading ${f.name} (${i + 1}/${newFiles.length})… ${p}%`));
        if (sid) stagedIds.push(sid);
      }
      setUploadStatus(newFiles.length ? "Sending…" : "");
      const { data } = await api.post(`/clients/${client.id}/email`, {
        subject, body, to, cc, bcc, thread_id: threadId,
        existing_file_ids: selectedDocs, staged_ids: stagedIds,
      });
      toast.success(data.fallback
        ? "Email sent (from shared mailbox; replies route to you)"
        : `Email sent from ${data.from}`);
      onOpenChange(false);
      onSent?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send email");
    } finally { setSending(false); setUploadStatus(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="email-compose-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight">Compose email</DialogTitle>
          <DialogDescription className="text-xs">
            Sending as <strong className="text-gray-700">{user?.email}</strong> via Microsoft 365.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-1">
          <div>
            <div className="flex items-center justify-between">
              <label className={labelCls}>To</label>
              {!showCc && (
                <button type="button" onClick={() => setShowCc(true)} data-testid="email-show-cc"
                  className="text-xs font-semibold text-[#002FA7] hover:underline">Add Cc / Bcc</button>
              )}
            </div>
            <input data-testid="email-to-input" value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com, another@example.com" className={inputCls} />
          </div>

          {showCc && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Cc</label>
                <input data-testid="email-cc-input" value={cc} onChange={(e) => setCc(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Bcc</label>
                <input data-testid="email-bcc-input" value={bcc} onChange={(e) => setBcc(e.target.value)} className={inputCls} />
              </div>
            </div>
          )}

          {templates.length > 0 && (
            <div>
              <label className={labelCls}>Template</label>
              <Select onValueChange={applyTemplate}>
                <SelectTrigger className="rounded-sm" data-testid="email-template-trigger">
                  <SelectValue placeholder="Start from a saved template…" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`email-template-${t.id}`}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label className={labelCls}>Subject</label>
            <input data-testid="email-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} />
          </div>

          <div>
            <label className={labelCls}>Message</label>
            <textarea data-testid="email-body-input" value={body} onChange={(e) => setBody(e.target.value)} rows={7}
              className={`${inputCls} resize-none`} placeholder="Write your message…" />
          </div>

          {docs.length > 0 && (
            <div>
              <label className={labelCls}>Attach client documents</label>
              <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto" data-testid="email-doc-list">
                {docs.map((d) => {
                  const on = selectedDocs.includes(d.id);
                  return (
                    <button key={d.id} type="button" onClick={() => toggleDoc(d.id)} data-testid={`email-doc-${d.id}`}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-sm border transition-colors flex items-center gap-1 ${
                        on ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-gray-600 border-gray-300 hover:border-[#002FA7]"}`}>
                      <Paperclip className="w-3 h-3" /> {d.original_filename}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className={labelCls}>Add new attachments <span className="font-normal lowercase text-gray-400">(also saved to client documents)</span></label>
            <input ref={fileRef} type="file" multiple onChange={addNewFiles} className="hidden" data-testid="email-file-input" />
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="email-add-file-button"
              className="flex items-center gap-1.5 text-sm font-semibold text-[#002FA7] border border-dashed border-gray-300 hover:border-[#002FA7] rounded-sm px-3 py-2 w-full justify-center">
              <Plus className="w-4 h-4" /> Add files
            </button>
            {newFiles.length > 0 && (
              <div className="mt-2 space-y-1" data-testid="email-newfiles-list">
                {newFiles.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="flex items-center justify-between text-xs bg-gray-50 border border-gray-100 rounded-sm px-2 py-1">
                    <span className="truncate">{f.name} <span className="text-gray-400">· {formatFileSize(f.size)}</span></span>
                    <button type="button" onClick={() => removeNewFile(i)} data-testid={`email-remove-newfile-${i}`} className="text-gray-400 hover:text-red-600 shrink-0 ml-2"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-400 mt-1.5">Total attachments must stay under 25 MB.</p>
          </div>
        </div>

        <DialogFooter className="pt-2">
          {sending && uploadStatus && (
            <span data-testid="email-upload-status" className="text-xs text-gray-500 mr-auto self-center truncate">{uploadStatus}</span>
          )}
          <button type="button" onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
          <button type="button" onClick={send} disabled={sending} data-testid="email-send-button"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send email
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
